import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { acceptanceTransferActsApi } from '@/features/acceptanceTransferActs/acceptanceTransferActsApi';
import { accountPaymentsApi } from '@/features/accountPayments/accountPaymentsApi';
import { addressesApi } from '@/features/addresses/addressesApi';
import { advanceInvoicesApi } from '@/features/advanceInvoices';
import { auditApi } from '@/features/audit';
import { authApi } from '@/features/auth/authApi';
import authSlice from '@/features/auth/authSlice';
import { biDashboardsApi } from '@/features/biDashboards/biDashboardsApi';
import { capacitySettingsApi } from '@/features/capacitySettings';
import { consumerCategoriesApi } from '@/features/consumerCategories';
import { contractAddendaApi } from '@/features/contractAddenda';
import { contractPricesApi } from '@/features/contractPrices';
import { contractTermsApi } from '@/features/contractTerms';
import { contractTypesApi } from '@/features/contractTypes/contractTypesApi';
import { contractVolumePricesApi } from '@/features/contractVolumePrices';
import { contractsApi } from '@/features/contracts';
import { counterpartiesApi } from '@/features/counterparties/counterpartiesApi';
import { counterpartyAccountsApi } from '@/features/counterpartyAccounts';
import { departmentsApi } from '@/features/departments/departmentsApi';
import { employeesApi } from '@/features/employees/employeesApi';
import { gasDistributionNetworkOperatorsApi } from '@/features/gasDistributionNetworkOperators';
import { gasTariffsApi } from '@/features/gasTariffs';
import { imbalanceSettingsApi } from '@/features/imbalanceSettings';
import { legalBasesApi } from '@/features/legalBases/legalBasesApi';
import { mainOrganizationApi } from '@/features/mainOrganization/mainOrganizationApi';
import { marginalPricesApi } from '@/features/marginalPrices';
import { marketGasPriceTypesApi } from '@/features/marketGasPrices/marketGasPriceTypesApi';
import { marketGasPricesApi } from '@/features/marketGasPrices/marketGasPricesApi';
import { meteringPointsApi } from '@/features/meteringPoints';
import { organizationApi } from '@/features/organization/organizationApi';
import { organizationBankAccountsApi } from '@/features/organizationBankAccounts/organizationBankAccountsApi';
import { organizationsApi } from '@/features/organizations/organizationsApi';
import { paymentImportsApi } from '@/features/paymentImports/paymentImportsApi';
import { periodLockApi } from '@/features/periodLock/periodLockApi';
import { permissionsApi } from '@/features/permissions';
import { plannedGasVolumesApi } from '@/features/plannedGasVolumes';
import { ponPriceSettingsApi } from '@/features/ponPriceSettings';
import { positionsApi } from '@/features/positions/positionsApi';
import { powerRateTariffTypesApi } from '@/features/powerRateTariffTypes/powerRateTariffTypesApi';
import { powerRateTariffsApi } from '@/features/powerRateTariffs/powerRateTariffsApi';
import { psgTariffProductTypesApi } from '@/features/psgTariffProductTypes/psgTariffProductTypesApi';
import { psgTariffsApi } from '@/features/psgTariffs/psgTariffsApi';
import { psoFormulaParametersApi } from '@/features/psoFormulaParameters/psoFormulaParametersApi';
import { psoFormulaParametersSettingsApi } from '@/features/psoFormulaParametersSettings';
import { psoFormulaVariablesApi } from '@/features/psoFormulaVariables/psoFormulaVariablesApi';
import { psoGasTariffsApi } from '@/features/psoGasTariffs/psoGasTariffsApi';
import { reportTemplatesApi } from '@/features/reportTemplates/reportTemplatesApi';
import { reportsApi } from '@/features/reports/reportsApi';
import { resourcesApi } from '@/features/resources/resourcesApi';
import { rolesApi } from '@/features/roles/rolesApi';
import { servicesApi } from '@/features/services/servicesApi';
import { supplyTariffTypesApi } from '@/features/supplyTariffTypes/supplyTariffTypesApi';
import { supplyTariffsApi } from '@/features/supplyTariffs/supplyTariffsApi';
import uiSlice from '@/features/ui/uiSlice';
import { undistributedPaymentsApi } from '@/features/undistributedPayments/undistributedPaymentsApi';

const rootReducer = combineReducers({
  [uiSlice.name]: uiSlice.reducer,
  [acceptanceTransferActsApi.reducerPath]: acceptanceTransferActsApi.reducer,
  [advanceInvoicesApi.reducerPath]: advanceInvoicesApi.reducer,
  [auditApi.reducerPath]: auditApi.reducer,
  [biDashboardsApi.reducerPath]: biDashboardsApi.reducer,
  [authSlice.name]: authSlice.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [counterpartiesApi.reducerPath]: counterpartiesApi.reducer,
  [contractTypesApi.reducerPath]: contractTypesApi.reducer,
  [undistributedPaymentsApi.reducerPath]: undistributedPaymentsApi.reducer,
  [periodLockApi.reducerPath]: periodLockApi.reducer,
  [counterpartyAccountsApi.reducerPath]: counterpartyAccountsApi.reducer,
  [gasTariffsApi.reducerPath]: gasTariffsApi.reducer,
  [imbalanceSettingsApi.reducerPath]: imbalanceSettingsApi.reducer,
  [legalBasesApi.reducerPath]: legalBasesApi.reducer,
  [marketGasPricesApi.reducerPath]: marketGasPricesApi.reducer,
  [marketGasPriceTypesApi.reducerPath]: marketGasPriceTypesApi.reducer,
  [psoFormulaParametersApi.reducerPath]: psoFormulaParametersApi.reducer,
  [psoFormulaVariablesApi.reducerPath]: psoFormulaVariablesApi.reducer,
  [psoGasTariffsApi.reducerPath]: psoGasTariffsApi.reducer,
  [psgTariffsApi.reducerPath]: psgTariffsApi.reducer,
  [psgTariffProductTypesApi.reducerPath]: psgTariffProductTypesApi.reducer,
  [supplyTariffsApi.reducerPath]: supplyTariffsApi.reducer,
  [supplyTariffTypesApi.reducerPath]: supplyTariffTypesApi.reducer,
  [powerRateTariffsApi.reducerPath]: powerRateTariffsApi.reducer,
  [powerRateTariffTypesApi.reducerPath]: powerRateTariffTypesApi.reducer,
  [permissionsApi.reducerPath]: permissionsApi.reducer,
  [servicesApi.reducerPath]: servicesApi.reducer,
  [consumerCategoriesApi.reducerPath]: consumerCategoriesApi.reducer,
  [contractsApi.reducerPath]: contractsApi.reducer,
  [contractAddendaApi.reducerPath]: contractAddendaApi.reducer,
  [meteringPointsApi.reducerPath]: meteringPointsApi.reducer,
  [plannedGasVolumesApi.reducerPath]: plannedGasVolumesApi.reducer,
  [contractPricesApi.reducerPath]: contractPricesApi.reducer,
  [contractTermsApi.reducerPath]: contractTermsApi.reducer,
  [contractVolumePricesApi.reducerPath]: contractVolumePricesApi.reducer,
  [employeesApi.reducerPath]: employeesApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
  [positionsApi.reducerPath]: positionsApi.reducer,
  [addressesApi.reducerPath]: addressesApi.reducer,
  [mainOrganizationApi.reducerPath]: mainOrganizationApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [accountPaymentsApi.reducerPath]: accountPaymentsApi.reducer,
  [paymentImportsApi.reducerPath]: paymentImportsApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [reportTemplatesApi.reducerPath]: reportTemplatesApi.reducer,
  [resourcesApi.reducerPath]: resourcesApi.reducer,
  [gasDistributionNetworkOperatorsApi.reducerPath]:
    gasDistributionNetworkOperatorsApi.reducer,
  [capacitySettingsApi.reducerPath]: capacitySettingsApi.reducer,
  [marginalPricesApi.reducerPath]: marginalPricesApi.reducer,
  [organizationBankAccountsApi.reducerPath]:
    organizationBankAccountsApi.reducer,
  [ponPriceSettingsApi.reducerPath]: ponPriceSettingsApi.reducer,
  [psoFormulaParametersSettingsApi.reducerPath]:
    psoFormulaParametersSettingsApi.reducer,
});

const middlewares = [
  acceptanceTransferActsApi.middleware,
  advanceInvoicesApi.middleware,
  auditApi.middleware,
  biDashboardsApi.middleware,
  authApi.middleware,
  organizationsApi.middleware,
  organizationApi.middleware,
  counterpartiesApi.middleware,
  contractTypesApi.middleware,
  undistributedPaymentsApi.middleware,
  periodLockApi.middleware,
  counterpartyAccountsApi.middleware,
  gasTariffsApi.middleware,
  imbalanceSettingsApi.middleware,
  legalBasesApi.middleware,
  marketGasPricesApi.middleware,
  marketGasPriceTypesApi.middleware,
  psoFormulaParametersApi.middleware,
  psoFormulaVariablesApi.middleware,
  psoGasTariffsApi.middleware,
  psgTariffsApi.middleware,
  psgTariffProductTypesApi.middleware,
  supplyTariffsApi.middleware,
  supplyTariffTypesApi.middleware,
  powerRateTariffsApi.middleware,
  powerRateTariffTypesApi.middleware,
  permissionsApi.middleware,
  servicesApi.middleware,
  consumerCategoriesApi.middleware,
  contractsApi.middleware,
  contractAddendaApi.middleware,
  meteringPointsApi.middleware,
  plannedGasVolumesApi.middleware,
  contractPricesApi.middleware,
  contractTermsApi.middleware,
  contractVolumePricesApi.middleware,
  employeesApi.middleware,
  departmentsApi.middleware,
  positionsApi.middleware,
  addressesApi.middleware,
  mainOrganizationApi.middleware,
  rolesApi.middleware,
  accountPaymentsApi.middleware,
  paymentImportsApi.middleware,
  reportsApi.middleware,
  reportTemplatesApi.middleware,
  resourcesApi.middleware,
  gasDistributionNetworkOperatorsApi.middleware,
  capacitySettingsApi.middleware,
  marginalPricesApi.middleware,
  organizationBankAccountsApi.middleware,
  ponPriceSettingsApi.middleware,
  psoFormulaParametersSettingsApi.middleware,
];

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

export default store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
